import * as React from 'react';
import { useCallback, useState } from 'react';

import { DataLakeQuery } from '@graylog/enterprise-api';

import { Button, Alert } from 'components/bootstrap';
import useFeature from 'hooks/useFeature';
import DataLakeRoutes from 'data-lake/Routes';
import useHistory from 'routing/useHistory';
import { ConfirmDialog } from 'components/common';
import { PREVIEW_JOB_STATUS } from 'data-lake/preview/Constants';
import UserNotification from 'util/UserNotification';

type StatusResponse =
  | { status: (typeof PREVIEW_JOB_STATUS)[keyof typeof PREVIEW_JOB_STATUS]; progress: number }
  | undefined;

type Props = React.PropsWithChildren<{
  bsStyle?: 'link';
  bsSize?: 'md' | 'xs';
  target?: '_blank';
  urlParams: string;
  dataLakeId: string;
  onClick?: () => void;
}>;

const PreviewLakeLogsButtonComponent = ({
  children = 'Preview logs',
  bsStyle = undefined,
  bsSize = 'xs',
  target = undefined,
  dataLakeId,
  urlParams,
  onClick = undefined,
}: Props) => {
  const history = useHistory();
  const [isLoadingActiveSearch, setIsLoadingActiveSearch] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  const onNavigate = useCallback(() => {
    const url = `${DataLakeRoutes.PREVIEW}?dataLakeId=${dataLakeId}${urlParams ? `&${urlParams}` : ''}`;
    if (target === '_blank') {
      window.open(url, target);
    } else {
      history.push(url);
    }
  }, [dataLakeId, urlParams, target, history]);

  const toggleShowConfirmation = useCallback(() => {
    setShowConfirmDialog((cur) => !cur);
  }, []);

  const onClickHandler = useCallback(() => {
    onClick?.();
    setIsLoadingActiveSearch(true);
    DataLakeQuery.searchJobExecutionState()
      .then(({ status }: StatusResponse) => {
        if (status === PREVIEW_JOB_STATUS.running) {
          toggleShowConfirmation();
        } else {
          onNavigate();
        }
      })
      .catch((error) => {
        if (error.status === 404) {
          return onNavigate();
        }

        return UserNotification.error(error.message);
      })
      .finally(() => setIsLoadingActiveSearch(false));
  }, [onClick, onNavigate, toggleShowConfirmation]);

  const onConfirm = useCallback(() => {
    DataLakeQuery.resetActiveQuery().then(() => onNavigate());
  }, [onNavigate]);

  return (
    <>
      <Button
        bsSize={bsSize}
        title="Preview data lake logs"
        onClick={onClickHandler}
        disabled={isLoadingActiveSearch}
        bsStyle={bsStyle}>
        {children}
      </Button>
      {showConfirmDialog && (
        <ConfirmDialog
          show={showConfirmDialog}
          onConfirm={onConfirm}
          onCancel={toggleShowConfirmation}
          title="A preview search is currently running">
          <Alert bsStyle="warning">
            A preview search is currently running. Continuing will cancel the ongoing search job. Do you want to
            proceed?
          </Alert>
        </ConfirmDialog>
      )}
    </>
  );
};

const PreviewLakeLogsButton = ({
  bsSize = undefined,
  bsStyle = undefined,
  children = undefined,
  dataLakeId,
  target = undefined,
  urlParams,
  onClick = undefined,
}: Props) => {
  const featureIsEnabled = useFeature('data_lake_search');
  if (!featureIsEnabled) {
    return null;
  }

  return (
    <PreviewLakeLogsButtonComponent
      urlParams={urlParams}
      bsStyle={bsStyle}
      bsSize={bsSize}
      target={target}
      dataLakeId={dataLakeId}
      onClick={onClick}>
      {children}
    </PreviewLakeLogsButtonComponent>
  );
};
export default PreviewLakeLogsButton;
